import { ScalarField, Point, Bounds, ImpactMap, ValueRange } from './scalar-field';
import { UTM } from '../map/projection';

import { Path as SVGPath } from '../svg/path';
import { Mercator } from '../map/projection';
import { SVGResult, TimedSVGResult } from '../map/svg-overlay-result'
import { looper } from '../util/util';
import { colorToString, ColorInterpolator } from '../util/color';

import { LineString } from './optimized-marching-squares';
import { SVGRoot, Color } from '../svg/svg'


export interface ScalarFieldIsolineRepresentation {
	datumIndex: number,
	zone: number,
	hemisphere: string,
	utmBounds: Bounds,
	isolines: { value: number, lines: LineString[][] }[],
	range: ValueRange,
	build: {
		start: number,
		end: number,
		time: number
	}
}

interface PathCommandsLineString {
	boundary: boolean,
	pathCommands?: (string | number)[][]
}

export interface IsolineSVGOptions {
	line?: {
		color: Color | ColorInterpolator,
		width: number
	},
	band?: {
		color: ColorInterpolator
	}
}

export interface ScalarFieldSVGResult extends TimedSVGResult  {
	range: ValueRange
}

export class Isoline {



	public static getIsolines(scalarField: ScalarField, renderParameters: { thresholds: number[], samplingStep?: Point }, smoothen: boolean, flatness: number): ScalarFieldIsolineRepresentation {
		const startTime = performance.now();

		scalarField.calculateIsoLines(renderParameters.thresholds, renderParameters.samplingStep);

		let isolineRepresentation = createIsolineRepresentation(scalarField, smoothen !== false, flatness);

		const endTime = performance.now();

		return Object.assign({
			build: {
				start: startTime,
				end: endTime,
				time: (endTime - startTime)
			}
		}, isolineRepresentation);
	}

	public static wrapInSVG = (svgRoot: SVGRoot) => `<svg viewBox="${svgRoot.viewBox.minX} ${svgRoot.viewBox.minY} ${svgRoot.viewBox.width} ${svgRoot.viewBox.height}" preserveAspectRatio="xMinYMin slice" style="width:100%; height:100%; position:absolute;">${svgRoot.content}</svg>`;

	//    public static toSVG = ( representation:ScalarFieldIsolineRepresentation, options: { debug?: boolean, smoothen?: boolean, line?: any, band?: any }, className?: string ) : MapOverlaySVGResult => {
	public static toSVG = (representation: ScalarFieldIsolineRepresentation, options: IsolineSVGOptions, className?: string): ScalarFieldSVGResult => {
		const startTime = performance.now();

		const projection = getProjection(representation.datumIndex, representation.zone, representation.hemisphere);
		let svgRepresentation = createSVGResult(representation.isolines, representation.utmBounds, projection, options, className);

		const endTime = performance.now();

		return Object.assign({
			build: {
				start: startTime,
				end: endTime,
				time: (endTime - startTime)
			},
			range: representation.range
		}, svgRepresentation);
	}

//	public static produceIsolineRepresentation = (scalarField: ScalarField, thresholds: number[], smoothen: boolean, flatness: number, sample?: Point): ScalarFieldIsolineRepresentation => {
//		const startTime = performance.now();
//
//		scalarField.calculateIsoLines(thresholds, sample)
//
//		let isolineRepresentation = createIsolineRepresentation(scalarField, smoothen !== false, flatness);
//
//		const endTime = performance.now();
//
//		return Object.assign({
//			build: {
//				start: startTime,
//				end: endTime,
//				time: (endTime - startTime)
//			}
//		}, isolineRepresentation);
//	}

	//used in mgmt-modeling for debugging imagery.
	//    public  getSVG() : MapOverlaySVGResult {
	public static getSVG(scalarField: ScalarField, renderParameters: { thresholds: number[], samplingStep?: Point }, svgOptions: IsolineSVGOptions): TimedSVGResult {
		return Isoline.toSVG( Isoline.getIsolines(scalarField, renderParameters, true, 5.0 ), svgOptions);

	}
}

const getProjection = (datumIndex: number, zone: number, hemisphere: string) => {
	return UTM.get(zone, hemisphere, datumIndex);
}

const getTransfomations = (projection: UTM, utmBounds: Bounds) => {

	const EARTH_RADIUS = projection.datum().radius();
	const EARTH_CIRCUMFERENCE = EARTH_RADIUS * 2 * Math.PI;
	const MAP_RESOLUTION = EARTH_CIRCUMFERENCE;
	const metreToMap = MAP_RESOLUTION / EARTH_CIRCUMFERENCE;

	const toMercator = (p: number[]) =>
		Mercator.fromLatLngToPointArray(
			projection.toGeodetic(
				[p[0],
				p[1]]
			),
			MAP_RESOLUTION)

	const mercatorOffset = projection.hemisphere() === UTM.HEMISPHERE_NORTH ?  toMercator([0, 0]) : toMercator([0, 10000000]);

	const toSVGMercator = (p: number[]) => {
		let pMercator = toMercator(p);
		pMercator[0] -= mercatorOffset[0];
		pMercator[1] -= mercatorOffset[1];
		return pMercator;
	}

	const min = toSVGMercator([utmBounds.x.min, utmBounds.y.min]);
	const max = toSVGMercator([utmBounds.x.max, utmBounds.y.max]);
	const size = [max[0] - min[0], max[1] - min[1]];

	let transform: (xy: number[]) => number[] = (xy: number[]) => [(xy[0] - min[0]), size[1] - (xy[1] - min[1])];


	return {
		metreToMap: metreToMap,
		toSVGMercator: toSVGMercator,
		transform: transform,
		offset: mercatorOffset,
		min: min,
		max: max,
		size: size,
		resolution: MAP_RESOLUTION
	}
}

const createIsolineRepresentation = (scalarField: ScalarField, smoothen: boolean, flatness: number) => {
	const isolineRepresentation = {
		datumIndex: scalarField.projection().datumIndex(),
		zone: scalarField.projection().zone(),
		hemisphere: scalarField.projection().hemisphere(),
		utmBounds: scalarField.utmBounds(),
		isolines: scalarField.getIsolines(),
		range: ( <ScalarField &{ range: () => ValueRange}>scalarField).range() ?  ( <ScalarField &{ range: () => ValueRange}>scalarField).range() : { min: undefined, max:undefined},
		impacts: <ImpactMap>undefined,
	};

	if (smoothen) {
		isolineRepresentation.isolines.forEach(valueIsolines => {
			valueIsolines.lines.forEach(isoline => {
				isoline.filter(lineString => !lineString.boundary)
					.forEach(lineString => {
						lineString.points = SVGPath.lineStringToSmoothenedLineString(lineString.points, flatness);
					})
			});
		});
	}

	return isolineRepresentation;
}



const createSVGResult = (sfIsoLines: { value: number, lines: LineString[][] }[], utmBounds: Bounds, projection: UTM, options: IsolineSVGOptions, className: string): SVGResult => {
	const transformations = getTransfomations(projection, utmBounds);

	//calculate the viewBox
	//Due to the use of Bezier curves we can overshoot the original bounds. So 
	//we widen the viewBox by a fraction of the dimension.
	const overShootFactor = 0.1; // results in a 20% wider/higher viewing area
	const dW = transformations.size[0] * overShootFactor;
	const dH = transformations.size[1] * overShootFactor;

	const xMin = transformations.min[0] - dW;
	const xMax = transformations.max[0] + dW;
	const yMin = transformations.min[1] - dH;
	const yMax = transformations.max[1] + dH;

	const origin = transformations.transform([xMin, yMax]);
	const width = xMax - xMin;
	const height = yMax - yMin;

	const southWest = Mercator.fromArrayToLatLng([xMin + transformations.offset[0], yMin + transformations.offset[1]], transformations.resolution);
	const nortEast = Mercator.fromArrayToLatLng([xMax + transformations.offset[0], yMax + transformations.offset[1]], transformations.resolution);

	const svgMarkup = createSVGMarkup(
		sfIsoLines,
		options,
		className,
		transformations.metreToMap,
		transformations.toSVGMercator,
		transformations.transform);

	const svgResult = {
		content: svgMarkup,
		viewBox: {
			minX: origin[0],
			minY: origin[1],
			width: width,
			height: height
		},
		southWest: {
			lat: southWest.lat,
			lng: southWest.lng
		},
		northEast: {
			lat: nortEast.lat,
			lng: nortEast.lng
		}
	};
	return svgResult;
}

const createSVGMarkup = (sfIsoLines: { value: number, lines: LineString[][] }[], options: IsolineSVGOptions & { debug?: boolean }, className: string, metreToMap: number, toSVGMercator: (p: number[]) => number[], transform: (xy: number[]) => number[]) => {

	const lineType = straight;

	const isolines = sfIsoLines
		.map((isoline) => {
			return {
				value: isoline.value,
				polygons: isoline.lines.map(polygon => {
					return polygon.map((lineString) => {
						return <PathCommandsLineString>{
							boundary: lineString.boundary,
							pathCommands: lineString.boundary ? straight(toSVGMercator, lineString) : lineType(toSVGMercator, lineString)
						}
					});
				})
			}
		});

	const isobandPaths = options.band ? isolines
		.map(isoline => { return { color: colorToString(options.band.color(isoline.value)), path: toIsobandPathAttribute(transform, isoline.polygons) }; })
		.map((isolinePathAttribute, index, list) => {
			if (index < list.length - 1) {
				return svgIsobandPath(isolinePathAttribute.path + list[index + 1].path, isolinePathAttribute.color, className);
			} else {
				return svgIsobandPath(isolinePathAttribute.path, isolinePathAttribute.color, className);
			}
		}) : [];

	const isolinePaths = options.line ? isolines
		.map(isoline => { return { color: options.line ? options.line.color ? (<Color>options.line.color).hue !== undefined ? colorToString(<Color>options.line.color) : colorToString((<ColorInterpolator>options.line.color)(isoline.value)) : '#808080' : colorToString(options.band.color(isoline.value)), path: toIsolinePathAttribute(transform, isoline.polygons) }; })
		.map((isolinePathAttribute) => {
			return svgIsolinePath(isolinePathAttribute.path, isolinePathAttribute.color, (options.line ? options.line.width ? options.line.width : 1 : 3) * metreToMap, className);
		}) : [];

	const isolinePoints = options.debug === true ? isolines
		.map(isoline => toIsolinePointCloud(transform, isoline.polygons, 5, className)) : [];

	const svgMarkup = isobandPaths.concat(isolinePaths, isolinePoints).join(' ');
	return svgMarkup;
}

const svgIsobandPath = (path: string, fill: string, className?: string) => `<path ${className ? `class="${className}" ` : ''}fill-rule="evenodd" d="${path}" fill="${fill}" stroke="none" />`;
const svgIsolinePath = (path: string, stroke: string, strokeWidth: number, className?: string) => `<path ${className ? `class="${className}" ` : ''}d="${path}" fill="none" stroke="${stroke}"  stroke-width="${strokeWidth}" stroke-linecap="round"/>`;

const toIsobandPathAttribute = (transform: (xy: number[]) => number[], polygons: PathCommandsLineString[][]) => {
	let svgPath = '';
	polygons.forEach(polygon =>
		polygon
			.map(lineString => lineString.pathCommands)
			.forEach((pathCommands, lineStringIndex) => {
				pathCommands.forEach((pathCommand, pathCommandIndex) => {
					let pc = pathCommand;
					if (lineStringIndex > 0 && pathCommandIndex === 0) {
						pc = [...pathCommand];//clone the path command because we need to alter it;
						pc[0] = 'L';
					}
					svgPath += ` ${transformPathCommand(pc, transform).join(' ')}`;
				});
			})
	);
	return svgPath;
}
const toIsolinePathAttribute = (transform: (xy: number[]) => number[], polygons: PathCommandsLineString[][]) => {
	let svgPath = '';
	polygons.forEach(polygon =>
		polygon
			.filter(lineString => !lineString.boundary)
			.map(lineString => lineString.pathCommands)
			.forEach(pathCommands => {
				pathCommands.forEach(pathCommand => {
					svgPath += ` ${transformPathCommand(pathCommand, transform).join(' ')}`;
				});
			})
	);
	return svgPath;
}

const toIsolinePointCloud = (transform: (xy: number[]) => number[], polygons: PathCommandsLineString[][], r: number, className?: string) => {
	let svg = '';
	const circle = (tpc: (string | number)[]) => `<circle ${className ? `class="${className}" ` : ''}cx="${tpc[0] === 'C' ? tpc[5] : tpc[1]}" cy="${tpc[0] === 'C' ? tpc[6] : tpc[2]}" r="${r}"/>`;
	polygons.forEach(polygon =>
		polygon
			.filter(lineString => !lineString.boundary)
			.map(lineString => lineString.pathCommands)
			.forEach(pathCommands => {
				pathCommands.forEach(pathCommand => {
					let tpc = transformPathCommand(pathCommand, transform);
					svg += ` ${circle(tpc)}`;
				});
			})
	);
	return svg;
}

const transformPathCommand = (pathCommand: (string | number)[], transform: (xy: number[]) => number[]) =>
	[pathCommand[0],
	...looper((pathCommand.length - 1) / 2).map(index => transform([<number>pathCommand[1 + index * 2], <number>pathCommand[2 + index * 2]])).array().flat()
	];

const straight = (transform: (p: number[]) => number[], lineString: LineString) => lineString.points.map((point, index) => [index === 0 ? 'M' : 'L', ...transform(point)]);

